/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEqual } from 'lodash';
import { KeyValue, unbox } from 'ngrx-forms';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

/* eslint-disable @angular-eslint/component-class-suffix */
import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SortChangedEvent,
} from '@ag-grid-community/core';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  ConfirmServiceInterface,
  CustomGridFilterParams,
  DataGridAction,
  EntityListField,
  FilterConfig,
  filterNullish,
  getExcelFields,
  getGridColumns,
  LazyEntityFacadeInterface,
  mapFromColumnState,
  mapFromFilterModel,
  mapToFilterModel,
  RouterFacadeInterface,
  SharedEntityFacadeInterface,
} from '@ideapa/app-common';
import { BaseDto } from '@ideapa/common-dto';
import { FieldConfig, FilterValue } from '@ideapa/common-interfaces';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';

import { BaseContainer } from './base.container';

@Component({
  standalone: false,
  template: '',
})
export abstract class BaseListContainer<T extends BaseDto, K extends KeyValue>
  extends BaseContainer
  implements OnInit
{
  abstract entity: string;

  @ViewChild('advancedFilters')
  private content?: any;

  private modalRef?: NgbModalRef;

  join?: string[];
  exportJoin?: string[];

  abstract fields$: Observable<EntityListField[]>;
  customActions$: Observable<DataGridAction[]> =
    this.routerFacade.isFindMode$.pipe(
      map((isFindMode) => {
        const actions: DataGridAction[] = [];
        if (isFindMode) {
          actions.push({
            description: 'seleziona',
            icon: 'add_task',
            onAction: (entity: any) => {
              this.select(entity);
            },
            visible: (entity: any) => {
              return entity.id != 0 && !entity.locked;
            },
          });
        }
        return actions;
      })
    );

  presetFilters$ = this.facade.queryParams$.pipe(
    map((queryParams) => {
      const filters = queryParams['filters'];
      if (!filters) return [];
      const decoded = atob(queryParams['filters']);
      const queryParamsFilters = JSON.parse(decoded) as FilterValue[];
      return queryParamsFilters;
    })
  );

  /**
   * Observable in ascolto sui parametri in Querystring che preleva
   * unicamente il parametro 'areDomainUserFiltersDisabled'.
   */
  areDomainUserFiltersDisabled$ = this.facade.queryParams$.pipe(
    map((queryParams) => {
      if (!queryParams['areDomainUserFiltersDisabled']) return false;
      const encodedDisableUserFilters =
        queryParams['areDomainUserFiltersDisabled'];
      const decodedDisableUserFilters = atob(encodedDisableUserFilters);
      return decodedDisableUserFilters === 'true';
    })
  );

  /**
   * Observable in ascolto sui parametri in Querystring che preleva
   * unicamente il parametro 'isEntitySelectionLimited'.
   */
  isEntitySelectionLimited$ = this.facade.queryParams$.pipe(
    map((queryParams) => {
      if (!queryParams['isEntitySelectionLimited']) return false;
      const encodedIsEntitySelectionLimited =
        queryParams['isEntitySelectionLimited'];
      const decodedIsEntitySelectionLimited = atob(
        encodedIsEntitySelectionLimited
      );
      return decodedIsEntitySelectionLimited === 'true';
    })
  );

  /**
   * Observable in ascolto sui parametri in Querystring che preleva
   * unicamente il parametro 'areEnteAnnoFiltersDisabled'.
   */
  areEnteAnnoFiltersDisabled$ = this.facade.queryParams$.pipe(
    map((queryParams) => {
      if (!queryParams['areEnteAnnoFiltersDisabled']) return false;
      const encodedAreEnteAnnoFiltersDisabled =
        queryParams['areEnteAnnoFiltersDisabled'];
      const decodedAreEnteAnnoFiltersDisabled = atob(
        encodedAreEnteAnnoFiltersDisabled
      );
      return decodedAreEnteAnnoFiltersDisabled === 'true';
    })
  );

  // abstract filters: FilterConfig[];
  // abstract colDefs: ColDef[];

  private _filters?: Observable<FilterConfig[]>;
  get filters$() {
    if (!this._filters) {
      this._filters = combineLatest([this.fields$, this.presetFilters$]).pipe(
        map(([fields, presetFilters]) =>
          fields
            .map((f) => ({
              ...f,
              readOnly:
                presetFilters.find((pf) => pf.field === f.field) != null,
            }))
            .filter(
              (f) =>
                !(
                  f.hideFilter &&
                  presetFilters.find((pf) => pf.field === f.field) == null
                )
            )
        )
      );
      // .map((f) => {
      //   return {
      //     elementLabel: f.elementLabel,
      //     field: f.field,
      //     filterType: f.filterType,
      //     elementValues$: f.elementValues$,
      //   } as FilterConfig;
      // });
    }
    return this._filters;
  }

  private _exportFields?: Observable<FieldConfig[]>;
  get exportFields$() {
    if (!this._exportFields) {
      this._exportFields = combineLatest([
        this.fields$,
        this.facade.getColumnState$.pipe(filterNullish()),
      ]).pipe(
        map(([fields, columnState]) => {
          return getExcelFields(fields, columnState);
        })
      );
    }
    return this._exportFields;
  }

  private _exportFilters?: Observable<FilterValue[]>;
  get exportFilters$() {
    if (!this._exportFilters) {
      this._exportFilters = this.facade.getFilterModel$.pipe(
        filterNullish(),
        withLatestFrom(this.presetFilters$),
        map(([filterModel, presetFilters]) => {
          const filters = mapFromFilterModel(filterModel);
          presetFilters.forEach((sf) => {
            const index = filters.findIndex(
              (f) =>
                f.field === sf.field &&
                f.type === sf.type &&
                f.operator === sf.operator &&
                f.value === sf.value
            );
            if (index >= 0) {
              filters[index] = sf;
            } else {
              filters.push(sf);
            }
          });
          return filters;
        })
      );
    }
    return this._exportFilters;
  }

  private _exportSort?: Observable<{ [field: string]: 'asc' | 'desc' }>;
  get exportSort$() {
    if (!this._exportSort) {
      this._exportSort = this.facade.getColumnState$.pipe(
        filterNullish(),
        map((cs) => {
          return mapFromColumnState(cs);
        })
      );
    }
    return this._exportSort;
  }

  private _columns?: Observable<ColDef[]>;
  get columnDefs$() {
    if (!this._columns) {
      this._columns = combineLatest([this.fields$, this.presetFilters$]).pipe(
        map(([fields, presetFilters]) => {
          return getGridColumns(
            fields.filter((f) => {
              return (
                !f.hideColumn ||
                presetFilters.find((pf) => pf.field === f.field) != null
              );
            }),
            [],
            []
          ).map((colDef) => {
            const f: EntityListField | undefined = fields.find(
              (f) => f.field == colDef.field
            );
            return {
              ...colDef,
              initialHide:
                f?.initialHideColumn &&
                presetFilters.find((pf) => pf.field === f?.field) == null,
              suppressFloatingFilterButton: true,
              floatingFilterComponentParams: {
                values: f?.elementValues,
                filterType: f?.filterType,
                field: f?.field,
                entityName: f?.entityName,
                hidden: f?.hideFilter,
                readOnly:
                  presetFilters.find((pf) => pf.field === f?.field) != null,
              } as CustomGridFilterParams,
            };
          });
        })
      );
    }
    return this._columns;
    // return this.entityFilters?.filter((ef) => ef.isFilter);
  }
  // columnDefs: ColDef[] = [];

  protected gridApi?: GridApi;

  commonGridOptions: GridOptions = {
    floatingFiltersHeight: 70,
    pagination: false,
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Colonne',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true,
            suppressColumnFilter: true,
          },
        },
      ],
    },
    suppressPaginationPanel: true,
    enableCellTextSelection: true,
    onGridReady: (e: GridReadyEvent) => this.onGridReady(e),
    onFilterChanged: (e: FilterChangedEvent) => this.onFilterChanged(e),
    onSortChanged: (e: SortChangedEvent) => this.onColumnStateChanged(e),
    onColumnVisible: (e: ColumnVisibleEvent) => this.onColumnStateChanged(e),
    onColumnMoved: (e: ColumnMovedEvent) => this.onColumnStateChanged(e),
    onColumnResized: (e: ColumnResizedEvent) => this.onColumnStateChanged(e),
    defaultColDef: {
      floatingFilter: true,
      resizable: true,
      filter: true,
      suppressHeaderMenuButton: true,
      floatingFilterComponent: 'customGridFilter',
    },
    columnTypes: {
      numberColumn: {
        filter: 'agNumberColumnFilter',
      },
      enumColumn: {
        cellRenderer: 'gridComponentEnumRenderer',
      },
      dateColumn: {
        filter: 'agDateColumnFilter',
      },
    },
  };

  filterFormState$ = this.facade.filterForm$;
  pageSize$ = this.facade.getPageSize$;
  currentPage$ = this.facade.getCurrentPage$;
  totalRows$ = this.facade.getTotalRows$;
  filterModel$ = this.facade.getFilterModel$;

  canDownloadExcel$ = this.sharedFacade.canRead$;
  canDelete$ = this.facade.canDelete$;
  canEdit$ = this.facade.canEdit$;
  canAdd$ = this.facade.canAdd$;
  canView$ = this.sharedFacade.canRead$;

  findMode$ = this.routerFacade.isFindMode$;
  modalListMode$ = this.routerFacade.isModalListMode$;

  gridOptions$!: Observable<GridOptions>;

  activeFiltersCount$ = this.filterModel$.pipe(
    map((model) => (model ? Object.entries(model).length : 0))
  );

  constructor(
    @Inject('SharedEntityFacade')
    protected sharedFacade: SharedEntityFacadeInterface<T>,
    @Inject('EntityFacade')
    protected facade: LazyEntityFacadeInterface<T, K>,
    @Inject('RouterFacade')
    protected routerFacade: RouterFacadeInterface,
    protected permissionsService: NgxPermissionsService,
    @Inject(NgbModal) protected modalService: NgbModal,
    @Inject('ConfirmService')
    protected confirmDialogService: ConfirmServiceInterface
  ) {
    super();
  }

  ngOnInit(): void {
    // stessi permessi di lettura

    this.gridOptions$ = this.columnDefs$.pipe(
      map(() => Object.assign(this.commonGridOptions, {}))
    );

    this.facade.showAdvancedFilters$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (show) => {
        if (show) {
          this.modalRef = this.modalService.open(this.content, {
            backdrop: 'static',
            size: 'xl',
          });

          try {
            await this.modalRef.result;
          } catch (err) {
            this.showAdvancedFilters(false);
          }
        } else {
          this.modalRef?.close();
        }
      });
    // set total rows  number se la facade espone il  numero di elementi
    this.facade.items$
      ?.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((items) => {
        this.facade.setTotalRows(items.length);
      });
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;

    this.pageSize$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((pageSize) => {
      this.pageSizeChanged(event.api, pageSize);
    });

    combineLatest([this.facade.getFilterModel$, this.presetFilters$])
      .pipe(
        withLatestFrom(this.filterFormState$),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([[filterModel, presetFilters], filterForm]) => {
        //console.log('filterModel', filterModel);
        const gridFilters = mapToFilterModel(presetFilters);
        filterModel = { ...filterModel, ...gridFilters };
        if (!isEqual(filterModel, this.gridApi?.getFilterModel())) {
          this.gridApi?.setFilterModel(filterModel);
        }

        const newFilters = mapFromFilterModel(filterModel);
        const filterFormValue = unbox(filterForm.value.filters);
        if (!isEqual(newFilters, filterFormValue)) {
          this.facade.setFilterFormValue(newFilters);
        }
      });

    this.facade.getColumnState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sortModel) => {
        if (
          sortModel &&
          !isEqual(sortModel, event.api.getColumnState())
        ) {
          event.api.applyColumnState({
            state: sortModel,
            applyOrder: true,
          });
        }
      });

    combineLatest([this.currentPage$, this.facade.pageLoaded$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([page, loaded]) => {
        if (loaded) {
          this.gridApi?.paginationGoToPage(page - 1);
        }
      });
  }

  onPageChange(newPage: number) {
    this.facade.setCurrentPage(newPage);
  }

  onPageSizeChanged(size: number) {
    this.facade.setPageSize(size);
  }

  pageSizeChanged(api: GridApi, pageSize: number) {
    api.updateGridOptions({ paginationPageSize: pageSize });
  }

  onColumnStateChanged(
    e:
      | SortChangedEvent
      | ColumnVisibleEvent
      | ColumnMovedEvent
      | ColumnResizedEvent
  ) {
    this.facade.setColumnState(e.api.getColumnState());
  }

  // Ricerca da griglia
  onFilterChanged(e: FilterChangedEvent) {
    this.facade.setFilterModel(e.api.getFilterModel());
    this.refreshTotals();
  }

  async refreshTotals() {
    if (this.gridApi && this.gridOptions$) {
      const go = await firstValueFrom(this.gridOptions$);
      if (!go.rowModelType || go.rowModelType === 'clientSide') {
        let filteresRows = 0;
        this.gridApi?.forEachNodeAfterFilter(() => filteresRows++);
        this.facade.setTotalRows(filteresRows);
      }
    }
  }
  // Ricerca da form esterno
  search(filters: FilterValue[]) {
    const externalFilters = mapToFilterModel(filters);
    this.facade.setFilterModel(externalFilters);

    this.showAdvancedFilters(false);
  }

  showAdvancedFilters(show: boolean) {
    this.facade.showAdvancedFilters(show);
  }

  async excel(fileName: string) {
    const columns = await firstValueFrom(this.exportFields$);
    const filters = await firstValueFrom(this.exportFilters$);
    const sort = await firstValueFrom(this.exportSort$);

    this.facade.downloadExcel(
      fileName,
      columns,
      this.exportJoin ? this.exportJoin : this.join,
      filters,
      sort
    );
  }

  // protected mapToFilterModel(
  //   filterModel: AgGridFilterModel,
  //   filters: FilterValue[]
  // ) {
  //   return filters.reduce((res, f) => {
  //     res[f.field] = {
  //       type: FilterOperator[f.operator],
  //       filterType: FilterType[f.type],
  //       filter: f.value.length ? f.value[0] : undefined,
  //     };
  //     return res;
  //   }, filterModel);
  // }

  // protected mapFromFilterModel(filterModel: AgGridFilterModel) {
  //   const filters: FilterValue[] = [];
  //   Object.keys(filterModel).forEach((key) => {
  //     const gridFilter = filterModel[key];
  //     filters.push({
  //       field: key,
  //       operator: (<any>FilterOperator)[gridFilter.type],
  //       type: (<any>FilterType)[gridFilter.filterType],
  //       value: [gridFilter.filter],
  //     });
  //   });
  //   return filters;
  // }

  add() {
    // this.facade.formInitialize(undefined);
    this.facade.addEntity();
    // this.facade.addEntity();
  }

  edit(entity: T) {
    this.routerFacade.navigateRelative([entity.id]);
    // this.routerFacade.router.navigateByUrl('./' + id);
  }

  view(entity: T) {
    this.routerFacade.navigateRelative([entity.id]);
    //this.routerFacade.viewEntityDetail(this.entity, entity.id);
  }

  async delete(entity: T) {
    const result = await this.confirmDialogService.confirm(
      `Confermi di voler eliminare l'elemento${
        (<any>entity).codice ? ' con codice ' + (<any>entity).codice : ''
      }?`
    );

    if (result) {
      this.sharedFacade.delete(entity.id, true, false, () => {
        // refresh lista paginata
        if (this.gridApi) {
          this.gridApi.refreshServerSide({}); // { route: route, purge: purge }
        }
      });
    }
  }

  select(item: T) {
    this.facade.selectItem(item);
  }

  refreshList() {
    // refresh lista paginata
    if (this.gridApi) {
      this.gridApi.refreshServerSide({});
    }
  }
}
